import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { CreditCard, Clock, CheckCircle, Star, Zap, Calendar, Package, AlertCircle, PauseCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBusiness } from '../contexts/BusinessContext';
import { db, functions } from '../config/firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Loading } from '../components/ui/Loading';
import { toast } from 'react-hot-toast';

interface PassType {
  id: string;
  passName: string;
  classCredits: number;
  validityInDays: number;
  price?: number;
  imageUrl?: string;
  isActive: boolean;
  businessId: string;
  timeSlotRestriction?: 'all_day' | 'morning' | 'afternoon';
  classTypeRestrictions?: string[];
}

interface Membership {
  id: string;
  name: string;
  price: number;
  reservationsPerMonth: number;
  imageUrl?: string;
  isActive: boolean;
  businessId: string;
  timeSlotRestriction?: 'all_day' | 'morning' | 'afternoon';
  classTypeRestrictions?: string[];
}

interface UserMembershipData {
  id: string;
  membershipId: string;
  membershipName: string;
  startDate: Date;
  renewalDate: Date;
  reservationsUsed: number;
  reservationsRemaining: number;
  reservationsPerMonth: number;
  timeRestriction: string;
  classRestriction: string;
  allowedClassTypeIds: string[];
  isActive: boolean;
  isPaused: boolean;
}

const UserMembershipsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { businessId } = useBusiness();
  const [passTypes, setPassTypes] = useState<PassType[]>([]);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [userMembership, setUserMembership] = useState<UserMembershipData | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentMode, setPaymentMode] = useState<'online' | 'in_person'>('in_person');
  const [stripeConnected, setStripeConnected] = useState(false);
  const [processingPayment, setProcessingPayment] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!businessId || !currentUser) {
        setLoading(false);
        return;
      }

      try {
        // Cargar configuración del negocio
        const businessDoc = await getDoc(doc(db, 'businesses', businessId));
        if (businessDoc.exists()) {
          const businessData = businessDoc.data();
          setPaymentMode(businessData.paymentMode || 'in_person');
          // Verificar si el negocio tiene Stripe conectado y activo
          setStripeConnected(businessData.stripeChargesEnabled === true);
        }

        // Cargar bonos disponibles
        const passTypesQuery = query(
          collection(db, 'passTypes'),
          where('businessId', '==', businessId),
          where('isActive', '==', true)
        );
        const passSnapshot = await getDocs(passTypesQuery);
        const passes = passSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as PassType[];
        passes.sort((a, b) => (a.price || 0) - (b.price || 0));
        setPassTypes(passes);

        // Cargar membresías disponibles
        const membershipsQuery = query(
          collection(db, 'memberships'),
          where('businessId', '==', businessId),
          where('isActive', '==', true)
        );
        const membershipSnapshot = await getDocs(membershipsQuery);
        const membershipList = membershipSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Membership[];
        membershipList.sort((a, b) => a.price - b.price);
        setMemberships(membershipList);

        // Cargar membresía del usuario actual
        const userMembershipQuery = query(
          collection(db, 'userMemberships'),
          where('userId', '==', currentUser.id),
          where('businessId', '==', businessId)
        );
        const userMembershipSnapshot = await getDocs(userMembershipQuery);

        if (!userMembershipSnapshot.empty) {
          const membershipDoc = userMembershipSnapshot.docs[0];
          const data = membershipDoc.data();

          // Obtener info del tipo de membresía
          const membershipInfoDoc = await getDoc(doc(db, 'memberships', data.membershipId));
          const membershipInfo = membershipInfoDoc.exists() ? membershipInfoDoc.data() : null;

          setUserMembership({
            id: membershipDoc.id,
            membershipId: data.membershipId,
            membershipName: membershipInfo?.name || 'Membresía',
            startDate: data.startDate?.toDate() || new Date(),
            renewalDate: data.renewalDate?.toDate() || new Date(),
            reservationsUsed: data.reservationsUsed || 0,
            reservationsRemaining: data.reservationsRemaining || 0,
            reservationsPerMonth: membershipInfo?.reservationsPerMonth || 0,
            timeRestriction: membershipInfo?.timeSlotRestriction || 'all_day',
            classRestriction: membershipInfo?.classTypeRestrictions?.length > 0 ? 'specific' : 'all',
            allowedClassTypeIds: membershipInfo?.classTypeRestrictions || [],
            isActive: data.isActive,
            isPaused: data.isPaused || false
          });
        }

      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [businessId, currentUser]);

  // Detectar parámetros de URL para mostrar mensajes de éxito/error
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const canceled = params.get('canceled');

    if (success === 'true') {
      toast.success('¡Compra realizada con éxito! 🎉');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (canceled === 'true') {
      toast.error('Pago cancelado');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Función para procesar la compra
  const handlePurchase = async (productType: 'membership' | 'pass', productId: string, productName: string, price: number) => {
    console.log('🛒 handlePurchase called:', { productType, productId, productName, price, businessId, currentUser: !!currentUser });

    if (!businessId || !currentUser) {
      console.error('❌ Missing businessId or currentUser:', { businessId, currentUser });
      toast.error('Error: No se pudo iniciar el pago. Intenta recargar la página.');
      return;
    }

    setProcessingPayment(productId);
    try {
      const createMarketplaceCheckout = httpsCallable(functions, 'createMarketplaceCheckout');
      const result = await createMarketplaceCheckout({
        businessId,
        productType,
        productId,
        productName,
        amount: Math.round(price * 100), // Convertir a céntimos
        successUrl: `${window.location.origin}/memberships?success=true`,
        cancelUrl: `${window.location.origin}/memberships?canceled=true`,
      });

      const data = result.data as { success: boolean; url: string };

      if (data.success && data.url) {
        window.open(data.url, '_blank');
      } else {
        toast.error('Error al procesar el pago');
      }
    } catch (error: any) {
      console.error('Error creating checkout:', error);
      toast.error(error.message || 'Error al procesar el pago');
    } finally {
      setProcessingPayment(null);
    }
  };

  const getPassGradient = (index: number) => {
    const gradients = [
      'from-blue-600 via-blue-500 to-cyan-500',
      'from-purple-600 via-purple-500 to-pink-500',
      'from-emerald-600 via-emerald-500 to-teal-500',
      'from-indigo-600 via-indigo-500 to-purple-500',
    ];
    return gradients[index % gradients.length];
  };

  const getMembershipGradient = (index: number) => {
    const gradients = [
      'from-amber-600 via-amber-500 to-orange-500',
      'from-rose-600 via-rose-500 to-pink-500',
      'from-teal-600 via-teal-500 to-cyan-500',
      'from-violet-600 via-violet-500 to-purple-500',
    ];
    return gradients[index % gradients.length];
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getDaysUntilRenewal = (date: Date) => {
    const now = new Date();
    const renewal = new Date(date);
    const diff = Math.ceil((renewal.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getTimeRestrictionLabel = (restriction: string) => {
    switch (restriction) {
      case 'morning': return '🌅 Solo mañanas (hasta 14:00)';
      case 'afternoon': return '🌆 Solo tardes (desde 14:00)';
      default: return '🕐 Todo el día';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loading size="lg" className="mx-auto mb-4" />
          <p className="text-gray-400">Cargando membresías y bonos...</p>
        </div>
      </div>
    );
  }

  const hasContent = memberships.length > 0 || passTypes.length > 0;

  // Verificar si tiene membresía (activa, pausada, o simplemente existe con datos válidos)
  const hasMembership = Boolean(
    // Desde userMemberships collection
    (userMembership && (userMembership.isActive !== false || userMembership.isPaused)) ||
    // Desde activeMembership en el usuario (si existe y tiene membershipId, cuenta)
    (currentUser?.activeMembership &&
      (currentUser.activeMembership.membershipId ||
        currentUser.activeMembership.isActive !== false ||
        currentUser.activeMembership.isPaused))
  );

  // Verificar si tiene bono (con créditos o no expirado)
  const hasPass = Boolean(
    currentUser?.activePass &&
    currentUser.activePass.passTypeId &&
    (currentUser.activePass.creditsRemaining > 0 ||
      !currentUser.activePass.expirationDate ||
      new Date(currentUser.activePass.expirationDate) > new Date())
  );

  const hasActivePlan = hasMembership || hasPass;

  // Debug en desarrollo
  console.log('🔍 Estado suscripciones:', {
    hasMembership,
    hasPass,
    userMembership: !!userMembership,
    activeMembership: !!currentUser?.activeMembership,
    activePass: !!currentUser?.activePass
  });

  return (
    <>
      <Helmet>
        <title>Membresías y Bonos</title>
      </Helmet>

      <div className="min-h-screen">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-amber-400 mb-4">
            Membresías y Bonos
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            {hasActivePlan ? 'Tu estado de suscripción actual' : 'Descubre los planes disponibles para tu entrenamiento'}
          </p>
        </div>

        {/* Estado actual del usuario - Membresía */}
        {(userMembership || currentUser?.activeMembership) && (
          <div className="max-w-4xl mx-auto mb-6 px-4">
            <div className={`rounded-3xl p-6 shadow-xl border ${(userMembership?.isPaused || currentUser?.activeMembership?.isPaused)
              ? 'bg-gradient-to-r from-yellow-900/50 to-orange-900/30 border-yellow-700/50'
              : (userMembership?.isActive || currentUser?.activeMembership?.isActive)
                ? 'bg-gradient-to-r from-amber-900/50 to-orange-900/30 border-amber-700/50'
                : 'bg-gradient-to-r from-gray-800 to-gray-700 border-gray-600'
              }`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-3 rounded-2xl ${(userMembership?.isPaused || currentUser?.activeMembership?.isPaused)
                  ? 'bg-yellow-500/20'
                  : (userMembership?.isActive || currentUser?.activeMembership?.isActive)
                    ? 'bg-amber-500/20'
                    : 'bg-gray-600/50'
                  }`}>
                  {(userMembership?.isPaused || currentUser?.activeMembership?.isPaused) ? (
                    <PauseCircle className="w-8 h-8 text-yellow-400" />
                  ) : (userMembership?.isActive || currentUser?.activeMembership?.isActive) ? (
                    <CreditCard className="w-8 h-8 text-amber-400" />
                  ) : (
                    <AlertCircle className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">
                    {(userMembership?.isPaused || currentUser?.activeMembership?.isPaused) ? 'Membresía Pausada' :
                      (userMembership?.isActive || currentUser?.activeMembership?.isActive) ? 'Tu Membresía Activa' :
                        'Membresía Pendiente'}
                  </h3>
                  <p className={`${(userMembership?.isPaused || currentUser?.activeMembership?.isPaused) ? 'text-yellow-400/80' : 'text-amber-400/80'}`}>
                    {userMembership?.membershipName || currentUser?.activeMembership?.membershipName || 'Cuota Mensual'}
                  </p>
                </div>
              </div>

              {userMembership ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Columna izquierda - Clases */}
                  <div className="bg-gray-900/50 rounded-xl p-4">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-gray-400 text-sm">Clases este mes</span>
                      <span className="text-white font-bold text-lg">
                        {/* Calcular clases usadas: total - restantes */}
                        {userMembership.reservationsPerMonth - userMembership.reservationsRemaining} / {userMembership.reservationsPerMonth}
                      </span>
                    </div>
                    <div className="h-3 bg-gray-700 rounded-full overflow-hidden mb-2">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${userMembership.reservationsRemaining === 0
                          ? 'bg-gradient-to-r from-red-500 to-red-600'
                          : 'bg-gradient-to-r from-amber-500 to-orange-500'
                          }`}
                        style={{ width: `${Math.min(((userMembership.reservationsPerMonth - userMembership.reservationsRemaining) / userMembership.reservationsPerMonth) * 100, 100)}%` }}
                      />
                    </div>
                    <p className={`text-sm ${userMembership.reservationsRemaining === 0 ? 'text-red-400' : 'text-gray-500'
                      }`}>
                      {userMembership.reservationsRemaining === 0
                        ? '¡Has agotado tus reservas este mes!'
                        : `${userMembership.reservationsRemaining} reservas restantes`}
                    </p>
                  </div>

                  {/* Columna derecha - Fechas y restricciones */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center bg-gray-900/30 rounded-lg px-4 py-2">
                      <span className="text-gray-400 text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Desde
                      </span>
                      <span className="text-white">{formatDate(userMembership.startDate)}</span>
                    </div>
                    <div className="flex justify-between items-center bg-gray-900/30 rounded-lg px-4 py-2">
                      <span className="text-gray-400 text-sm flex items-center gap-2">
                        <RefreshCw className="w-4 h-4" />
                        Renueva
                      </span>
                      <span className={`font-medium ${getDaysUntilRenewal(userMembership.renewalDate) <= 3 ? 'text-red-400' :
                        getDaysUntilRenewal(userMembership.renewalDate) <= 7 ? 'text-yellow-400' : 'text-white'
                        }`}>
                        {formatDate(userMembership.renewalDate)}
                        <span className="text-gray-500 text-xs ml-1">
                          ({getDaysUntilRenewal(userMembership.renewalDate)} días)
                        </span>
                      </span>
                    </div>
                    <div className="flex justify-between items-center bg-gray-900/30 rounded-lg px-4 py-2">
                      <span className="text-gray-400 text-sm flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Horario
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${userMembership.timeRestriction === 'all_day' ? 'bg-green-500/20 text-green-400' :
                        userMembership.timeRestriction === 'morning' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-purple-500/20 text-purple-400'
                        }`}>
                        {getTimeRestrictionLabel(userMembership.timeRestriction)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                // Membresía básica (sin userMemberships, solo con campos del usuario)
                <div className="space-y-3">
                  <div className="flex justify-between items-center bg-gray-900/30 rounded-lg px-4 py-2">
                    <span className="text-gray-400 text-sm">Estado</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${currentUser?.paymentStatus === 'paid'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                      {currentUser?.paymentStatus === 'paid' ? 'Pagada' : 'Pendiente de pago'}
                    </span>
                  </div>
                  {currentUser?.membershipStartDate && (
                    <div className="flex justify-between items-center bg-gray-900/30 rounded-lg px-4 py-2">
                      <span className="text-gray-400 text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Desde
                      </span>
                      <span className="text-white">{formatDate(new Date(currentUser.membershipStartDate))}</span>
                    </div>
                  )}
                  {currentUser?.nextDueDate && (
                    <div className="flex justify-between items-center bg-gray-900/30 rounded-lg px-4 py-2">
                      <span className="text-gray-400 text-sm flex items-center gap-2">
                        <RefreshCw className="w-4 h-4" />
                        Vence
                      </span>
                      <span className={`font-medium ${getDaysUntilRenewal(new Date(currentUser.nextDueDate)) <= 3 ? 'text-red-400' :
                        getDaysUntilRenewal(new Date(currentUser.nextDueDate)) <= 7 ? 'text-yellow-400' : 'text-white'
                        }`}>
                        {formatDate(new Date(currentUser.nextDueDate))}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Estado actual del usuario - Bono Activo */}
        {currentUser?.activePass && currentUser.activePass.creditsRemaining > 0 && (
          <div className="max-w-4xl mx-auto mb-10 px-4">
            <div className="bg-gradient-to-r from-primary-900/50 to-blue-900/30 rounded-3xl p-6 shadow-xl border border-primary-700/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-primary-500/20 rounded-2xl">
                    <CheckCircle className="w-8 h-8 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">Tu Bono Activo</h3>
                    <p className="text-primary-400/80">
                      {currentUser.activePass.passTypeName || currentUser.activePass.passName || 'Bono de clases'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-white">
                    {currentUser.activePass.creditsRemaining}
                  </div>
                  <div className="text-primary-400/70 text-sm">clases restantes</div>
                </div>
              </div>

              {/* Detalles del bono */}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-gray-900/50 rounded-xl p-3 text-center">
                  <div className="text-gray-400 text-xs mb-1">Clases usadas</div>
                  <div className="text-white font-bold">
                    {(currentUser.activePass.totalCredits || currentUser.activePass.creditsRemaining) - currentUser.activePass.creditsRemaining}
                  </div>
                </div>
                <div className="bg-gray-900/50 rounded-xl p-3 text-center">
                  <div className="text-gray-400 text-xs mb-1">Total créditos</div>
                  <div className="text-white font-bold">{currentUser.activePass.totalCredits || currentUser.activePass.creditsRemaining}</div>
                </div>
                {currentUser.activePass.expirationDate && (
                  <div className="bg-gray-900/50 rounded-xl p-3 text-center col-span-2">
                    <div className="text-gray-400 text-xs mb-1">Vence</div>
                    <div className={`font-bold ${getDaysUntilRenewal(new Date(currentUser.activePass.expirationDate)) <= 7 ? 'text-red-400' : 'text-white'
                      }`}>
                      {formatDate(new Date(currentUser.activePass.expirationDate))}
                    </div>
                  </div>
                )}
              </div>

              {/* Barra de progreso */}
              <div className="mt-4">
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary-500 to-blue-500 rounded-full transition-all duration-500"
                    style={{
                      width: `${((currentUser.activePass.totalCredits || currentUser.activePass.creditsRemaining) - currentUser.activePass.creditsRemaining) / (currentUser.activePass.totalCredits || currentUser.activePass.creditsRemaining) * 100}%`
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sin plan activo */}
        {!hasActivePlan && (
          <div className="max-w-4xl mx-auto mb-10 px-4">
            <div className="bg-gray-800/50 rounded-3xl p-8 border border-gray-700 text-center">
              <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-gray-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Sin suscripción activa</h3>
              <p className="text-gray-400">
                No tienes ninguna membresía o bono activo. ¡Explora las opciones disponibles!
              </p>
            </div>
          </div>
        )}

        {!hasContent ? (
          <div className="text-center py-16">
            <CreditCard className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">
              No hay membresías o bonos disponibles en este momento
            </p>
          </div>
        ) : (
          <>
            {/* Separador */}
            <div className="max-w-4xl mx-auto px-4 mb-8">
              <div className="border-t border-gray-800"></div>
              <p className="text-center text-gray-500 text-sm mt-4">Planes disponibles</p>
            </div>

            {/* Sección de Membresías */}
            {memberships.length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-amber-400 mb-6 px-4 flex items-center gap-2">
                  <CreditCard className="w-6 h-6" />
                  Membresías Mensuales
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto px-4">
                  {memberships.map((membership, index) => (
                    <div
                      key={membership.id}
                      className="group relative rounded-3xl overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
                    >
                      {/* Imagen de fondo o gradiente */}
                      {membership.imageUrl ? (
                        <div className="absolute inset-0">
                          <img
                            src={membership.imageUrl}
                            alt={membership.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
                        </div>
                      ) : (
                        <>
                          <div className={`absolute inset-0 bg-gradient-to-br ${getMembershipGradient(index)} opacity-90`} />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                        </>
                      )}

                      {/* Efecto hover */}
                      <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-300 pointer-events-none" />

                      {/* Contenido */}
                      <div className="relative p-6 min-h-[300px] flex flex-col">
                        {/* Badge */}
                        <div className="mb-4 px-3 py-1 bg-amber-500/30 backdrop-blur-sm rounded-full w-fit">
                          <span className="text-amber-300 text-xs font-semibold uppercase tracking-wider">
                            Mensual
                          </span>
                        </div>

                        {/* Icono */}
                        <div className="mb-4 p-3 bg-white/20 backdrop-blur-sm rounded-2xl w-fit">
                          <CreditCard className="w-8 h-8 text-white" />
                        </div>

                        {/* Nombre */}
                        <h3 className="text-2xl font-bold text-white mb-2">
                          {membership.name}
                        </h3>

                        {/* Detalles */}
                        <div className="space-y-2 mb-4 flex-grow">
                          <div className="flex items-center gap-2 text-white/90">
                            <Calendar className="w-4 h-4" />
                            <span className="text-sm">{membership.reservationsPerMonth} reservas por mes</span>
                          </div>
                          <div className="flex items-center gap-2 text-white/90">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm">
                              {membership.timeSlotRestriction === 'morning' ? 'Solo mañanas' :
                                membership.timeSlotRestriction === 'afternoon' ? 'Solo tardes' : 'Todo el día'}
                            </span>
                          </div>
                          {membership.classTypeRestrictions && membership.classTypeRestrictions.length > 0 && (
                            <div className="flex items-center gap-2 text-white/90">
                              <Star className="w-4 h-4" />
                              <span className="text-sm">Clases específicas</span>
                            </div>
                          )}
                        </div>

                        {/* Precio y botón */}
                        <div className="mt-auto pt-4 border-t border-white/20">
                          <div className="flex items-baseline gap-1 mb-3">
                            <span className="text-3xl font-bold text-white">{membership.price}€</span>
                            <span className="text-white/70">/mes</span>
                          </div>

                          {paymentMode === 'online' && stripeConnected ? (
                            hasMembership ? (
                              <div className="text-center py-2 px-3 bg-green-500/20 backdrop-blur-sm rounded-xl border border-green-500/30">
                                <p className="text-green-400 text-sm font-medium">✓ Ya tienes una membresía activa</p>
                              </div>
                            ) : (
                              <button
                                className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePurchase('membership', membership.id, membership.name, membership.price);
                                }}
                                disabled={processingPayment === membership.id}
                              >
                                {processingPayment === membership.id ? (
                                  <>
                                    <Loading size="sm" />
                                    Procesando...
                                  </>
                                ) : (
                                  <>
                                    <CreditCard className="w-4 h-4" />
                                    Contratar
                                  </>
                                )}
                              </button>
                            )
                          ) : (
                            <div className="text-center py-2 px-3 bg-white/10 backdrop-blur-sm rounded-xl">
                              <p className="text-white/80 text-sm">Contacta con recepción</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Borde */}
                      <div className="absolute inset-0 rounded-3xl border border-white/10 pointer-events-none" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sección de Bonos */}
            {passTypes.length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-primary-400 mb-6 px-4 flex items-center gap-2">
                  <Package className="w-6 h-6" />
                  Bonos de Clases
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto px-4">
                  {passTypes.map((passType, index) => (
                    <div
                      key={passType.id}
                      className="group relative rounded-3xl overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
                    >
                      {/* Imagen de fondo o gradiente */}
                      {passType.imageUrl ? (
                        <div className="absolute inset-0">
                          <img
                            src={passType.imageUrl}
                            alt={passType.passName}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
                        </div>
                      ) : (
                        <>
                          <div className={`absolute inset-0 bg-gradient-to-br ${getPassGradient(index)} opacity-90`} />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                        </>
                      )}

                      {/* Efecto hover */}
                      <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-300 pointer-events-none" />

                      {/* Contenido */}
                      <div className="relative p-6 min-h-[280px] flex flex-col">
                        {/* Icono */}
                        <div className="mb-4 p-3 bg-white/20 backdrop-blur-sm rounded-2xl w-fit">
                          <Package className="w-8 h-8 text-white" />
                        </div>

                        {/* Nombre */}
                        <h3 className="text-2xl font-bold text-white mb-2">
                          {passType.passName}
                        </h3>

                        {/* Detalles */}
                        <div className="space-y-2 mb-4 flex-grow">
                          <div className="flex items-center gap-2 text-white/90">
                            <Zap className="w-4 h-4" />
                            <span className="text-sm">{passType.classCredits} reservas incluidas</span>
                          </div>
                          <div className="flex items-center gap-2 text-white/90">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm">Válido {passType.validityInDays} días desde contratación</span>
                          </div>
                          {passType.timeSlotRestriction && passType.timeSlotRestriction !== 'all_day' && (
                            <div className="flex items-center gap-2 text-white/90">
                              <Clock className="w-4 h-4" />
                              <span className="text-sm">
                                {passType.timeSlotRestriction === 'morning' ? 'Solo mañanas' : 'Solo tardes'}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Precio y botón */}
                        <div className="mt-auto pt-4 border-t border-white/20">
                          {passType.price && (
                            <div className="flex items-baseline gap-1 mb-3">
                              <span className="text-3xl font-bold text-white">{passType.price}€</span>
                            </div>
                          )}

                          {paymentMode === 'online' && stripeConnected && passType.price ? (
                            hasPass ? (
                              <div className="text-center py-2 px-3 bg-blue-500/20 backdrop-blur-sm rounded-xl border border-blue-500/30">
                                <p className="text-blue-400 text-sm font-medium">✓ Ya tienes un bono activo</p>
                              </div>
                            ) : (
                              <button
                                className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePurchase('pass', passType.id, passType.passName, passType.price!);
                                }}
                                disabled={processingPayment === passType.id}
                              >
                                {processingPayment === passType.id ? (
                                  <>
                                    <Loading size="sm" />
                                    Procesando...
                                  </>
                                ) : (
                                  <>
                                    <CreditCard className="w-4 h-4" />
                                    Comprar Bono
                                  </>
                                )}
                              </button>
                            )
                          ) : (
                            <div className="text-center py-2 px-3 bg-white/10 backdrop-blur-sm rounded-xl">
                              <p className="text-white/80 text-sm">Contacta con recepción</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Borde */}
                      <div className="absolute inset-0 rounded-3xl border border-white/10 pointer-events-none" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Nota informativa */}
        <div className="max-w-4xl mx-auto mt-12 px-4">
          <div className={`backdrop-blur-sm rounded-2xl p-6 border text-center ${paymentMode === 'online'
            ? 'bg-green-900/30 border-green-800'
            : 'bg-gray-900/60 border-gray-800'
            }`}>
            {paymentMode === 'online' ? (
              <div className="flex items-center justify-center gap-3">
                <CreditCard className="w-5 h-5 text-green-400" />
                <p className="text-green-400">
                  Puedes contratar directamente desde esta página
                </p>
              </div>
            ) : (
              <p className="text-gray-400">
                📍 Para adquirir una membresía o bono, contacta con la recepción del centro
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UserMembershipsPage;
